import { EnumProfiloSelezionatoTipo } from '@framework/api-client';
import type { PersonaGiuridicaDTO } from '@framework/api-client';
import { useAuth, useCurrentUser } from '@framework/auth';
import { useMemo } from 'react';

export type SelectedLegalEntity = Pick<
  PersonaGiuridicaDTO,
  | 'id'
  | 'ragioneSociale'
  | 'partitaIva'
  | 'tipoPg'
  | 'pec'
  | 'indirizzo'
  | 'comune'
  | 'provincia'
  | 'cap'
  | 'paeseSedeLegale'
  | 'statoAccreditamento'
  | 'flAccreditata'
  | 'procedimentiAbilitati'
>;

export interface UseSelectedLegalEntityResult {
  selectedLegalEntity: SelectedLegalEntity | null;
}

export type IUseSelectedLegalEntity = () => UseSelectedLegalEntityResult;

/**
 * PG attualmente attiva ("operare per conto di"), derivata direttamente da
 * `profiloSelezionato` (unica fonte di verità). È valorizzata solo a login
 * completato (`isLogged`): durante la selezione/riselezione l'utente non sta
 * ancora operando per conto di nessuno, quindi resta null.
 */
export const useSelectedLegalEntity: IUseSelectedLegalEntity = () => {
  const { isLogged } = useAuth();
  const profile = useCurrentUser()?.utenteLoggato?.profiloSelezionato;

  const selectedLegalEntity = useMemo<SelectedLegalEntity | null>(() => {
    if (!isLogged) return null;
    if (profile?.tipo !== EnumProfiloSelezionatoTipo.PG) return null;
    const entity = profile.personaGiuridica;
    if (!entity) return null;
    return {
      id: entity.id,
      ragioneSociale: entity.ragioneSociale,
      partitaIva: entity.partitaIva,
      tipoPg: entity.tipoPg,
      pec: entity.pec,
      indirizzo: entity.indirizzo,
      comune: entity.comune,
      provincia: entity.provincia,
      cap: entity.cap,
      paeseSedeLegale: entity.paeseSedeLegale,
      statoAccreditamento: entity.statoAccreditamento,
      flAccreditata: entity.flAccreditata,
      procedimentiAbilitati: entity.procedimentiAbilitati,
    };
  }, [isLogged, profile]);

  return { selectedLegalEntity };
};
